import type { JSONContent } from '@sequencehq/core-components'
import type { QuoteBlock, TextBlockConfig } from '@sequencehq/quote-content'
import type { PresentationV3 } from 'Cube/domain/cube.domain.types'
import type { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import { match } from 'ts-pattern'
import { createPhasesBlock } from '../../utils/createPhasesBlock'
import { imageAdapter } from './image'
import { joinAdjacentLists, listsAdapter } from './lists'
import { tableAdapter } from './table'
import { textContentAdapter } from './textContent'
import {
  textBlockAttributesIn,
  textBlockAttributesOut
} from './textBlockAttributes'

type SaveContext = { pricingBlock: () => QuoteBlock | undefined }

type TextBlockType = 'paragraph' | 'heading1' | 'heading2' | 'heading3'

export const PRICING_CUSTOM_BLOCK = 'pricing'

/**
 * Converts between the saved quote presentation and the editor's document.
 * Block types the editor doesn't know are dropped on load.
 */
export const presentationToTiptap = {
  in: (presentation: PresentationV3): JSONContent => {
    const content = blocksIn(presentation.blocks)

    return {
      type: 'doc',
      content: content.length > 0 ? content : [{ type: 'paragraph' }]
    }
  },
  out:
    ({ cubeQueries }: { cubeQueries: CubeDomainInterface['queries'] }) =>
    (doc: JSONContent): QuoteBlock[] =>
      blocksOut({ pricingBlock: () => createPhasesBlock(cubeQueries) })(
        doc.content ?? []
      ),
  /**
   * The blocks the editor would save straight after loading, with the pricing
   * block as it was saved. Older list formats load as today's, so comparing
   * against this, rather than the saved blocks, stops opening a quote from
   * saving it. They're only rewritten alongside a real change.
   */
  asLoaded: (presentation: PresentationV3): QuoteBlock[] =>
    blocksOut({
      pricingBlock: () =>
        presentation.blocks.find(block => block.blockType === 'phases')
    })(presentationToTiptap.in(presentation).content ?? [])
}

const blocksIn = (blocks: QuoteBlock[]): JSONContent[] =>
  joinAdjacentLists(blocks.flatMap(blockIn))

const blockIn = (block: QuoteBlock): JSONContent[] =>
  match(block.blockType)
    .with('coverImage', 'pdfCoverImage', () => [])
    .with('paragraph', () => [textBlockIn(block, { type: 'paragraph' })])
    .with('heading1', () => [textBlockIn(block, { type: 'heading', level: 1 })])
    .with('heading2', () => [textBlockIn(block, { type: 'heading', level: 2 })])
    .with('heading3', () => [textBlockIn(block, { type: 'heading', level: 3 })])
    .with('bulletList', 'numberList', () => [listsAdapter.in(block, blocksIn)])
    .with('divider', () => [{ type: 'divider' }])
    .with('image', () => [imageAdapter.in(block)])
    .with('table', () => tableAdapter.in(block))
    .with('phases', () => [
      { type: 'customBlock', attrs: { blockType: PRICING_CUSTOM_BLOCK } }
    ])
    .otherwise(() => [])

const textBlockIn = (
  block: QuoteBlock,
  node: { type: 'paragraph' } | { type: 'heading'; level: 1 | 2 | 3 }
): JSONContent => {
  const config = block.config as TextBlockConfig

  return {
    type: node.type,
    attrs: {
      ...('level' in node && { level: node.level }),
      ...textBlockAttributesIn(config)
    },
    content: textContentAdapter.in(config.text)
  }
}

const blocksOut =
  (ctx: SaveContext) =>
  (nodes: JSONContent[]): QuoteBlock[] =>
    nodes.flatMap(node =>
      match(node.type)
        .with('paragraph', () => [textBlockOut(node, 'paragraph')])
        .with('heading', () => [
          textBlockOut(node, headingBlockType(node.attrs?.level))
        ])
        .with('bulletList', 'orderedList', () =>
          listsAdapter.out(node, blocksOut(ctx))
        )
        .with('divider', () => [DIVIDER_BLOCK])
        .with('image', () => [imageAdapter.out(node)])
        .with('table', () => [tableAdapter.out(node)])
        .with('customBlock', () => customBlockOut(ctx)(node))
        .otherwise(() => [])
    )

const DIVIDER_BLOCK: QuoteBlock = {
  blockType: 'divider',
  config: {},
  version: 1
}

const headingBlockType = (level: number | undefined): TextBlockType =>
  match(level)
    .with(1, () => 'heading1' as const)
    .with(2, () => 'heading2' as const)
    .otherwise(() => 'heading3' as const)

const textBlockOut = (
  node: JSONContent,
  blockType: TextBlockType
): QuoteBlock => ({
  blockType,
  config: {
    text: textContentAdapter.out(node.content),
    ...textBlockAttributesOut(node.attrs)
  },
  version: 1
})

/**
 * The pricing block is a copy of the quote's pricing, so it's rebuilt on every
 * save to pick up pricing edited outside the editor.
 */
const customBlockOut =
  (ctx: SaveContext) =>
  (node: JSONContent): QuoteBlock[] =>
    match(node.attrs?.blockType)
      .with(PRICING_CUSTOM_BLOCK, () => {
        const pricingBlock = ctx.pricingBlock()

        return pricingBlock ? [pricingBlock] : []
      })
      .otherwise(() => [])
